interface Lead {
  sector: string | null;
  lead_context: string | null;
  action_type: string | null;
}

interface DistributionChartsProps {
  leads: Lead[];
}

const COLORS = ['#f0b429', '#34d399', '#f472b6', '#fb923c', '#a78bfa', '#38bdf8', '#facc15'];

function DonutChart({ data, total }: { data: { label: string; count: number; color: string }[]; total: number }) {
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = 44;
  const strokeW = 18;
  const circumference = 2 * Math.PI * r;

  let offset = 0;
  const segments = data.map((d) => {
    const pct = total > 0 ? d.count / total : 0;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const seg = { ...d, dash, gap, offset };
    offset += dash;
    return seg;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeW} />
      {segments.map((seg, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={seg.color}
          strokeWidth={strokeW}
          strokeDasharray={`${seg.dash} ${seg.gap}`}
          strokeDashoffset={-seg.offset + circumference * 0.25}
          strokeLinecap="butt"
          style={{ transition: 'stroke-dasharray 0.7s ease' }}
        />
      ))}
      <text x={cx} y={cy - 4} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="900">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#d1d5db" fontSize="9">toplam</text>
    </svg>
  );
}

function buildData(leads: Lead[], key: keyof Lead): { label: string; count: number; color: string }[] {
  const map: Record<string, number> = {};
  leads.forEach((l) => {
    const val = (l[key] as string | null) || 'Diğer';
    map[val] = (map[val] || 0) + 1;
  });
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7)
    .map(([label, count], i) => ({ label, count, color: COLORS[i % COLORS.length] }));
}

export default function DistributionCharts({ leads }: DistributionChartsProps) {
  const total = leads.length;

  const sectorData = buildData(leads, 'sector');
  const sourceData = buildData(leads, 'lead_context');
  const actionData = buildData(leads, 'action_type');

  const charts = [
    { title: 'Sektör Dağılımı', data: sectorData },
    { title: 'Kaynak Dağılımı', data: sourceData },
    { title: 'Hedef Aksiyon', data: actionData },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {charts.map(({ title, data }) => (
        <div
          key={title}
          className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <h3 className="text-white font-bold text-sm mb-4">{title}</h3>
          {data.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Henüz veri yok</p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <DonutChart data={data} total={total} />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                {data.map((d) => (
                  <div key={d.label} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: d.color }}
                    />
                    <span className="text-gray-200 text-xs truncate flex-1">{d.label}</span>
                    <span className="text-white text-xs font-bold flex-shrink-0">
                      {total > 0 ? Math.round((d.count / total) * 100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
